import { useEffect, useState } from "react";
import { X, ArrowRight, Loader2, Folder, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";

/**
 * Move material(s) to another project.
 *
 * Props:
 *   - listingIds: string[]  (1 or many — bulk if length > 1)
 *   - currentProjectId?: string (used to grey out "no change")
 *   - onClose(): close without saving
 *   - onMoved(result): called with backend response after success
 */
export default function MoveMaterialModal({ listingIds, currentProjectId, onClose, onMoved }) {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isBulk = listingIds.length > 1;

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProjects([]));
  }, []);

  const submit = async () => {
    if (target === "" && !currentProjectId) {
      toast.error(t("move.pickTarget"));
      return;
    }
    if (target === (currentProjectId || "") && !isBulk) {
      toast.error(t("move.sameTarget"));
      return;
    }
    setSubmitting(true);
    try {
      const targetValue = target === "__detach" ? null : target;
      let result;
      if (isBulk) {
        const r = await api.post("/listings/bulk-move", {
          listing_ids: listingIds,
          to_project_id: targetValue,
          note,
        });
        result = r.data;
        toast.success(t("move.bulkSuccess").replace("{moved}", String(result.moved)).replace("{total}", String(result.total)));
      } else {
        const r = await api.post(`/listings/${listingIds[0]}/move`, {
          to_project_id: targetValue,
          note,
        });
        result = r.data;
        toast.success(t("move.success"));
      }
      onMoved?.(result);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("move.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  // Surface a "Lagret"-type project at the top if one exists by name.
  const sortedProjects = [...projects].sort((a, b) => {
    const aw = /lager|warehouse/i.test(a.namn || "") ? 0 : 1;
    const bw = /lager|warehouse/i.test(b.namn || "") ? 0 : 1;
    return aw - bw;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="move-material-modal">
      <div className="bg-[var(--bone)] w-full max-w-lg border border-[var(--ink-200)] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[var(--ink-200)]">
          <div>
            <div className="label-tech">{isBulk ? t("move.bulkLabel") : t("move.label")}</div>
            <h3 className="font-display text-2xl tracking-tight mt-1">
              {isBulk ? t("move.bulkTitle").replace("{n}", String(listingIds.length)) : t("move.title")}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-500)] hover:text-[var(--ink)]" data-testid="move-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="label-tech">{t("move.targetProject")}</label>
            <select
              className="input-tech mt-2"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="move-target-select"
            >
              <option value="">{t("move.targetPlaceholder")}</option>
              <option value="__detach">{t("move.targetDetach")}</option>
              {sortedProjects.length > 0 && <option disabled>──────────</option>}
              {sortedProjects.map((p) => {
                const isWarehouse = /lager|warehouse/i.test(p.namn || "");
                return (
                  <option key={p.id} value={p.id} disabled={!isBulk && p.id === currentProjectId}>
                    {isWarehouse ? "🏬 " : ""}{p.namn}{!isBulk && p.id === currentProjectId ? ` (${t("move.currentMark")})` : ""}
                  </option>
                );
              })}
            </select>
            {target && target !== "__detach" && /lager|warehouse/i.test((sortedProjects.find((p) => p.id === target)?.namn) || "") && (
              <div className="mt-2 label-tech text-[var(--ink-500)] inline-flex items-center gap-2">
                <Warehouse size={12} /> {t("move.warehouseHint")}
              </div>
            )}
          </div>

          <div>
            <label className="label-tech">{t("move.note")}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={300}
              className="input-tech mt-2"
              placeholder={t("move.notePh")}
              data-testid="move-note-input"
            />
          </div>
        </div>

        <div className="p-5 border-t border-[var(--ink-200)] flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost" data-testid="move-cancel-btn">
            {t("common.cancel")}
          </button>
          <button onClick={submit} className="btn-primary" disabled={submitting} data-testid="move-submit-btn">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {submitting ? t("move.moving") : t("move.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
